import { forwardRef } from "react";
import tokens from "../../tokens.js";

export const responsiveIFrameContainer = (ratio) => {
  const ratioStyles =
    ratio < 0.75
      ? {
          paddingTop: `${ratio * 100}%`,
          width: "100%",
        }
      : {
          width: `${20 / ratio}rem`,
          height: "20rem",
          marginLeft: "auto",
          marginRight: "auto",
          paddingTop: 0,
        };

  return {
    ...ratioStyles,
    position: "relative",
    overflow: "hidden",
    borderRadius: tokens.radius.small,
  };
};

const responsiveIFramePlayer = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
};

/**
 * ResponsiveIFrame is a React functional component that renders an iframe element
 * with a responsive aspect ratio. It utilizes the forwardRef to allow passing a ref
 * to the iframe for direct DOM interactions.
 *
 * @param {Object} props - The properties object for configuring the ResponsiveIFrame.
 * @param {number} [props.ratio=0.5625] - The aspect ratio for the video or content in
 * the iframe. Defaults to 16:9 (0.5625).
 * @param {string} [props.title] - A title for the iframe content, used to improve
 * accessibility.
 * @param {Object} [props.iframeRef] - A ref object that will be attached to the iframe
 * element, which can be used for direct manipulation of the iframe DOM node.
 * @param {Object} [props.props] - Additional props are spread onto the iframe element,
 * allowing for further customization such as specifying the src, sandbox attributes, etc.
 *
 * @returns {JSX.Element} A JSX element containing a div that wraps an iframe, styled to
 * maintain the specified aspect ratio while filling its parent container's width.
 */
export const ResponsiveIFrame = forwardRef(
  ({ ratio = 0.5625, title, ...props }, iframeRef) => {
    return (
      <div style={responsiveIFrameContainer(ratio)}>
        <iframe
          ref={iframeRef}
          title={title || ""}
          width="100%"
          height="100%"
          style={responsiveIFramePlayer}
          allowFullScreen
          loading="lazy"
          {...props}
        />
      </div>
    );
  },
);
